/**
 * Central de Projetos: conversas do WhatsApp da equipe, no desenho do
 * WhatsApp Web (lista à esquerda, conversa à direita; no celular, uma de cada
 * vez). Ler aqui nunca marca nada como lido: tudo vem do que o monitor já
 * gravou. Quem pode responder escreve, anexa e grava áudio pelo WhatsApp da
 * equipe, também sem marcar como lida; marcar é um botão à parte.
 *
 * - `/central-projetos/conversas/:conversaId` abre direto uma conversa;
 *   `?alerta=projetos` abre a lista já no filtro Projetos (atalho do sino).
 * - A frase da IA aparece na lista; o botão da prévia mostra o cartão sem abrir.
 * - Quem trata a Central escolhe o tipo, liga a conversa ao pedido e marca o
 *   alerta Projetos como tratado.
 * - ESC fecha a conversa e devolve o foco à linha dela na lista.
 */
import React, { useCallback, useEffect, useState } from "react";
import { FiEyeOff, FiMessageCircle } from "react-icons/fi";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";
import { useValorComAtraso } from "../../hooks/useBuscaRemota";
import useMediaQuery from "../../hooks/useMediaQuery";
import { listarConversas } from "../../services/centralProjetos";
import { PERM, useCarga } from "./comum";
import { CELULAR, ColunaConversa, IconeDaVazia, Moldura, NotaCentral, Tela, Vazia } from "./conversas/estilos";
import ListaDeConversas, { filtroPeloId } from "./conversas/ListaDeConversas";
import PainelDaConversa from "./conversas/PainelDaConversa";

const BASE = "/central-projetos/conversas";

function ConversaVazia() {
  return (
    <Vazia>
      <div>
        <IconeDaVazia><FiMessageCircle size={30} aria-hidden="true" /></IconeDaVazia>
        <h2>Conversas do WhatsApp da equipe</h2>
        <p>Escolha uma conversa para ler as mensagens e a prévia da IA: quem está esperando quem, o que falta e o próximo passo.</p>
        <small><FiEyeOff size={13} aria-hidden="true" /> Ler aqui não marca nada como lido no WhatsApp.</small>
      </div>
    </Vazia>
  );
}

function mesmoId(a, b) {
  return a !== undefined && a !== null && String(a) === String(b);
}

/** Depois de fechar a conversa, o foco volta à linha dela (quem usa teclado continua de onde estava). */
function focarNaLista(id) {
  window.requestAnimationFrame(() => {
    document.querySelector(`[data-conversa-id="${String(id).replace(/"/g, "")}"]`)?.focus();
  });
}

export default function ConversasCentral() {
  const { conversaId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const celular = useMediaQuery(CELULAR);
  const [parametros] = useSearchParams();
  const [filtro, setFiltro] = useState(() => (parametros.get("alerta") === "projetos" ? "projetos" : "todas"));
  const [buscaDigitada, setBuscaDigitada] = useState("");
  const busca = useValorComAtraso(buscaDigitada.trim());
  const { consulta } = filtroPeloId(filtro);
  const { dados, erro, carregando, recarregar, alterar } = useCarga(
    (request) => listarConversas(request, { ...consulta, busca }),
    [filtro, busca],
  );
  // A conversa aberta continua aberta mesmo se sair do filtro (ex.: acabou de ser classificada).
  const [aberta, setAberta] = useState(null);
  const conversas = dados ? dados.conversas || [] : null;
  const daLista = conversas?.find((c) => mesmoId(c.id, conversaId)) || null;
  const conversa = daLista || (aberta && mesmoId(aberta.id, conversaId) ? aberta : null);

  useEffect(() => {
    if (daLista) setAberta(daLista);
  }, [daLista]);

  const atualizarItem = useCallback(
    (item, corpo) => {
      if (!item?.id) return;
      setAberta((atual) => (atual && mesmoId(atual.id, item.id) ? { ...atual, ...item } : item));
      alterar((atuais) => ({
        ...atuais,
        conversas: (atuais.conversas || []).map((c) => (mesmoId(c.id, item.id) ? { ...c, ...item } : c)),
      }));
      // Mudou o tipo ou o alerta: as contagens dos filtros mudaram junto.
      if (corpo && ("classificacao" in corpo || "alerta" in corpo)) recarregar();
    },
    [alterar, recarregar],
  );

  const podeTratar = Boolean(hasPermission?.(PERM.TRATAR));
  const podeLer = Boolean(hasPermission?.(PERM.MENSAGENS));
  const podeResponder = Boolean(hasPermission?.(PERM.RESPONDER));
  // No celular é uma coisa de cada vez, como no app; a lista só se esconde.
  const listaEscondida = celular && Boolean(conversaId);
  const mostrarConversa = !celular || Boolean(conversaId);
  const fecharConversa = useCallback(() => {
    navigate(BASE);
    focarNaLista(conversaId);
  }, [navigate, conversaId]);

  let painel;
  if (!conversaId) painel = <ConversaVazia />;
  else if (conversa) {
    painel = (
      <PainelDaConversa
        key={conversa.id}
        conversa={conversa}
        podeTratar={podeTratar}
        podeLer={podeLer}
        podeResponder={podeResponder}
        aoVoltar={celular ? fecharConversa : null}
        aoFechar={fecharConversa}
        aoAtualizarItem={atualizarItem}
      />
    );
  } else if (carregando || !dados) {
    painel = <Vazia aria-busy="true" />;
  } else {
    painel = (
      <Vazia>
        <div>
          <NotaCentral>Esta conversa não está na lista. Volte para Todas ou procure pelo nome.</NotaCentral>
        </div>
      </Vazia>
    );
  }

  return (
    <Tela>
      <Moldura>
        <ListaDeConversas
          conversas={conversas}
          contagens={dados?.contagens}
          carregando={carregando}
          erro={erro}
          filtro={filtro}
          aoFiltrar={setFiltro}
          busca={buscaDigitada}
          aoBuscar={setBuscaDigitada}
          ativaId={conversaId}
          aoAbrir={(c) => navigate(`${BASE}/${c.id}`)}
          aoVoltar={() => navigate("/central-projetos")}
          aoAtualizarItem={atualizarItem}
          escondida={listaEscondida}
        />
        {mostrarConversa ? <ColunaConversa aria-label="Conversa aberta">{painel}</ColunaConversa> : null}
      </Moldura>
    </Tela>
  );
}
